/**
 * Mathematical utilities for enhanced Monte Carlo simulation
 * Fixes mathematical correctness issues identified in audit
 */

export class MathUtils {
  /**
   * Safe square root that prevents NaN from negative inputs
   */
  static safeSqrt(value: number): number {
    return Math.sqrt(Math.max(0, value));
  }

  /**
   * Clamps a value between min and max bounds
   */
  static clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  /**
   * Generates a proper Cholesky decomposition for correlation matrix
   * Uses simplified 2x2 approach for income/capital components
   */
  static generateCholeskyFactors(correlation: number): { l11: number; l21: number; l22: number } {
    // Ensure correlation is valid
    const validCorr = this.clamp(correlation, -0.99, 0.99);
    
    // Cholesky decomposition for 2x2 matrix
    // | 1   corr |   | l11  0  | | l11 l21 |
    // | corr  1  | = | l21 l22 | |  0  l22 |
    
    const l11 = 1;
    const l21 = validCorr;
    const l22 = this.safeSqrt(1 - validCorr * validCorr);
    
    return { l11, l21, l22 };
  }

  /**
   * Generates correlated random variables using Cholesky decomposition
   */
  static generateCorrelatedPair(
    random1: number, 
    random2: number, 
    correlation: number
  ): { x: number; y: number } {
    const { l11, l21, l22 } = this.generateCholeskyFactors(correlation);
    
    const x = l11 * random1;
    const y = l21 * random1 + l22 * random2;
    
    return { x, y };
  }

  /**
   * Validates that a correlation matrix is positive semi-definite
   */
  static validateCorrelationMatrix(correlations: number[][]): boolean {
    // For a 2x2 matrix, check that determinant >= 0
    if (correlations.length === 2 && correlations[0].length === 2) {
      const det = correlations[0][0] * correlations[1][1] - 
                  correlations[0][1] * correlations[1][0];
      return det >= -1e-10; // Small tolerance for floating point errors
    }
    
    // For larger matrices, would need proper eigenvalue decomposition
    return true;
  }

  /**
   * Calculates percentile value from sorted array
   */
  static percentile(sortedArray: number[], p: number): number {
    if (sortedArray.length === 0) return 0;
    
    const index = (p / 100) * (sortedArray.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    
    if (lower === upper) {
      return sortedArray[lower];
    }
    
    const weight = index - lower;
    return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
  }

  /**
   * Calculates standard deviation of an array
   */
  static standardDeviation(values: number[]): number {
    if (values.length === 0) return 0;
    
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const squaredDifferences = values.map(val => Math.pow(val - mean, 2));
    const variance = squaredDifferences.reduce((sum, val) => sum + val, 0) / values.length;
    
    return Math.sqrt(variance);
  }

  /**
   * Calculates Sharpe ratio
   */
  static sharpeRatio(returns: number[], riskFreeRate: number = 0.025): number {
    if (returns.length === 0) return 0;
    
    const avgReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
    const excessReturn = avgReturn - riskFreeRate;
    const stdDev = this.standardDeviation(returns);
    
    return stdDev === 0 ? 0 : excessReturn / stdDev;
  }
}